<template>
  <div class="new-contract-panel">
    <div class="new-contract-panel__header">
      <span class="new-contract-panel__title">Nuevo contrato</span>
      <button class="new-contract-panel__close-btn" @click="$emit('close')">
        <span class="mdi mdi-close"></span>
      </button>
    </div>

    <div class="new-contract-panel__content">
      <div v-if="errorMsg" class="new-contract-panel__error">{{ errorMsg }}</div>

      <!-- Nave -->
      <section class="new-contract-panel__section">
        <span class="new-contract-panel__section-title">Nave</span>
        <select v-model="form.plotId" class="new-contract-panel__input">
          <option value="" disabled>Selecciona una nave disponible</option>
          <option v-for="p in availablePlots" :key="p.id" :value="p.id">
            {{ p.name }}{{ p.areaM2 ? ` — ${p.areaM2} m²` : '' }}
          </option>
        </select>
        <span v-if="!loadingPlots && availablePlots.length === 0" class="new-contract-panel__hint">
          No hay naves disponibles en este momento.
        </span>
      </section>

      <!-- Inquilino -->
      <section class="new-contract-panel__section">
        <span class="new-contract-panel__section-title">Inquilino</span>

        <div class="new-contract-panel__toggle">
          <button
            class="new-contract-panel__toggle-btn"
            :class="{ 'new-contract-panel__toggle-btn--active': tenantMode === 'existing' }"
            @click="tenantMode = 'existing'"
          >
            Existente
          </button>
          <button
            class="new-contract-panel__toggle-btn"
            :class="{ 'new-contract-panel__toggle-btn--active': tenantMode === 'new' }"
            @click="tenantMode = 'new'"
          >
            Nuevo inquilino
          </button>
        </div>

        <div v-if="tenantMode === 'existing'" class="new-contract-panel__tenant-search">
          <input
            v-model="tenantQuery"
            type="text"
            placeholder="Buscar inquilino por nombre..."
            class="new-contract-panel__input"
            @input="onTenantSearchInput"
          />
          <div v-if="selectedTenant" class="new-contract-panel__selected-tenant">
            {{ selectedTenant.name }}
            <button class="new-contract-panel__clear-tenant" @click="clearSelectedTenant">
              <span class="mdi mdi-close"></span>
            </button>
          </div>
          <div v-else-if="tenantResults.length" class="new-contract-panel__tenant-results">
            <button
              v-for="t in tenantResults"
              :key="t.id"
              class="new-contract-panel__tenant-result"
              @click="selectTenant(t)"
            >
              {{ t.name }}
            </button>
          </div>
        </div>

        <div v-else class="new-contract-panel__grid">
          <input v-model="newTenant.name" placeholder="Nombre / razón comercial *" class="new-contract-panel__input" />
          <input v-model="newTenant.razonSocial" placeholder="Razón social" class="new-contract-panel__input" />
          <input v-model="newTenant.rfc" placeholder="RFC" class="new-contract-panel__input" />
          <input v-model="newTenant.contactEmail" placeholder="Correo de contacto" class="new-contract-panel__input" />
          <input v-model="newTenant.contactPhone" placeholder="Teléfono de contacto" class="new-contract-panel__input" />
        </div>
      </section>

      <!-- Condiciones -->
      <section class="new-contract-panel__section">
        <span class="new-contract-panel__section-title">Condiciones del contrato</span>
        <div class="new-contract-panel__grid">
          <div class="new-contract-panel__field">
            <span class="new-contract-panel__field-label">Renta mensual (MXN) *</span>
            <input v-model.number="form.monthlyRent" type="number" class="new-contract-panel__input" />
          </div>
          <div class="new-contract-panel__field">
            <span class="new-contract-panel__field-label">Día de pago</span>
            <input v-model.number="form.paymentDay" type="number" min="1" max="31" placeholder="Predeterminado" class="new-contract-panel__input" />
          </div>
          <div class="new-contract-panel__field">
            <span class="new-contract-panel__field-label">Fecha de inicio *</span>
            <input v-model="form.startDate" type="date" class="new-contract-panel__input" />
          </div>
          <div class="new-contract-panel__field">
            <span class="new-contract-panel__field-label">Fecha de vencimiento *</span>
            <input v-model="form.endDate" type="date" class="new-contract-panel__input" />
          </div>
          <div class="new-contract-panel__field">
            <span class="new-contract-panel__field-label">Depósito en garantía</span>
            <input v-model.number="form.depositAmount" type="number" class="new-contract-panel__input" />
          </div>
          <div class="new-contract-panel__field">
            <span class="new-contract-panel__field-label">Estatus del depósito</span>
            <select v-model="form.depositStatus" class="new-contract-panel__input">
              <option value="">—</option>
              <option value="HELD">Recibido</option>
              <option value="PENDING">Pendiente</option>
            </select>
          </div>
          <div class="new-contract-panel__field">
            <span class="new-contract-panel__field-label">Tipo de contrato</span>
            <select v-model="form.contractType" class="new-contract-panel__input">
              <option value="FIXED">Renta fija</option>
              <option value="VARIABLE">Renta variable</option>
              <option value="MIXED">Mixto</option>
            </select>
          </div>
        </div>
      </section>

      <!-- Saldo anterior a Parkos (opcional) -->
      <section class="new-contract-panel__section">
        <span class="new-contract-panel__section-title">Saldo pendiente anterior a Parkos (opcional)</span>
        <div class="new-contract-panel__grid">
          <div class="new-contract-panel__field">
            <span class="new-contract-panel__field-label">Monto que ya debe (MXN)</span>
            <input v-model.number="form.openingBalanceAmount" type="number" min="0" class="new-contract-panel__input" placeholder="0" />
          </div>
          <div class="new-contract-panel__field">
            <span class="new-contract-panel__field-label">Vencido desde</span>
            <input v-model="form.openingBalanceDate" type="date" class="new-contract-panel__input" />
          </div>
        </div>
        <span class="new-contract-panel__field-label">Se registra como un solo cargo, "Saldo anterior a Parkos". Si dejas la fecha vacía, vence hoy.</span>
      </section>

      <!-- Escalaciones (opcional) -->
      <section class="new-contract-panel__section">
        <div class="new-contract-panel__section-header">
          <span class="new-contract-panel__section-title">Escalaciones programadas (opcional)</span>
          <button class="new-contract-panel__add-escalation" @click="addEscalationRow">+ Agregar</button>
        </div>
        <div v-for="(e, i) in form.escalations" :key="i" class="new-contract-panel__escalation-row">
          <input v-model="e.date" type="date" class="new-contract-panel__input" />
          <input v-model.number="e.newRent" type="number" placeholder="Nueva renta" class="new-contract-panel__input" />
          <button class="new-contract-panel__remove-escalation" @click="form.escalations.splice(i, 1)">
            <span class="mdi mdi-close"></span>
          </button>
        </div>
      </section>
    </div>

    <div class="new-contract-panel__footer">
      <button class="new-contract-panel__cancel-btn" @click="$emit('close')">Cancelar</button>
      <button class="new-contract-panel__submit-btn" :disabled="submitting" @click="onSubmit">
        {{ submitting ? 'Creando...' : 'Crear contrato' }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { getAvailablePlots, createContract } from '@/services/contracts'
import { searchTenants } from '@/services/tenants'

const props = defineProps({
  parkId: { type: String, required: true },
})

const emit = defineEmits(['close', 'created'])

const availablePlots = ref([])
const loadingPlots = ref(true)
const errorMsg = ref('')
const submitting = ref(false)

const tenantMode = ref('existing')
const tenantQuery = ref('')
const tenantResults = ref([])
const selectedTenant = ref(null)
let tenantDebounce = null

const newTenant = ref({ name: '', razonSocial: '', rfc: '', contactEmail: '', contactPhone: '' })

const form = ref({
  plotId: '',
  monthlyRent: null,
  paymentDay: null,
  startDate: '',
  endDate: '',
  depositAmount: null,
  depositStatus: '',
  contractType: 'FIXED',
  escalations: [],
  openingBalanceAmount: null,
  openingBalanceDate: '',
})

onMounted(async () => {
  try {
    availablePlots.value = await getAvailablePlots(props.parkId)
  } finally {
    loadingPlots.value = false
  }
})

function onTenantSearchInput() {
  selectedTenant.value = null
  if (tenantDebounce) clearTimeout(tenantDebounce)
  if (!tenantQuery.value.trim()) {
    tenantResults.value = []
    return
  }
  tenantDebounce = setTimeout(async () => {
    tenantResults.value = await searchTenants(tenantQuery.value)
  }, 250)
}

function selectTenant(t) {
  selectedTenant.value = t
  tenantResults.value = []
}

function clearSelectedTenant() {
  selectedTenant.value = null
  tenantQuery.value = ''
}

function addEscalationRow() {
  form.value.escalations.push({ date: '', newRent: null })
}

async function onSubmit() {
  errorMsg.value = ''

  if (!form.value.plotId) return (errorMsg.value = 'Selecciona una nave')
  if (!form.value.monthlyRent) return (errorMsg.value = 'La renta mensual es requerida')
  if (!form.value.startDate || !form.value.endDate) return (errorMsg.value = 'Las fechas de inicio y vencimiento son requeridas')
  if (tenantMode.value === 'existing' && !selectedTenant.value) return (errorMsg.value = 'Selecciona un inquilino existente')
  if (tenantMode.value === 'new' && !newTenant.value.name) return (errorMsg.value = 'El nombre del inquilino es requerido')

  submitting.value = true
  try {
    const payload = {
      plotId: form.value.plotId,
      startDate: form.value.startDate,
      endDate: form.value.endDate,
      monthlyRent: form.value.monthlyRent,
      paymentDay: form.value.paymentDay || null,
      depositAmount: form.value.depositAmount || null,
      depositStatus: form.value.depositStatus || null,
      contractType: form.value.contractType,
      escalations: form.value.escalations.filter((e) => e.date && e.newRent),
      ...(form.value.openingBalanceAmount > 0
        ? { openingBalance: { amount: form.value.openingBalanceAmount, dueDate: form.value.openingBalanceDate || null } }
        : {}),
      ...(tenantMode.value === 'existing' ? { tenantId: selectedTenant.value.id } : { newTenant: newTenant.value }),
    }
    const created = await createContract(payload)
    emit('created', created)
  } catch (error) {
    errorMsg.value = error.message
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.new-contract-panel {
  flex: 0 0 40%;
  max-width: 40%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid var(--border-active);
  border-radius: 10px;
  overflow: hidden;
  margin: 0 1.5rem 1.5rem 0;
  min-height: 0;
}

.new-contract-panel__header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1.25rem;
  border-bottom: 1px solid #f1f5f9;
}

.new-contract-panel__title {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 16px;
  color: #1e3a5f;
}

.new-contract-panel__close-btn {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
}

.new-contract-panel__content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 1rem 1.25rem;
}

.new-contract-panel__content::-webkit-scrollbar {
  width: 6px;
}

.new-contract-panel__content::-webkit-scrollbar-thumb {
  background: var(--border-active);
  border-radius: 3px;
}

.new-contract-panel__content::-webkit-scrollbar-track {
  background: transparent;
}

.new-contract-panel__error {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  font-family: var(--font-body);
  font-size: 12px;
  margin-bottom: 1rem;
}

.new-contract-panel__section {
  margin-bottom: 1.25rem;
}

.new-contract-panel__section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.new-contract-panel__section-title {
  display: block;
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #94a3b8;
  margin-bottom: 0.5rem;
}

.new-contract-panel__hint {
  font-family: var(--font-body);
  font-size: 11px;
  color: #94a3b8;
}

.new-contract-panel__input {
  width: 100%;
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--text-content);
  background: #f8f9fc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.45rem 0.6rem;
  box-sizing: border-box;
}

.new-contract-panel__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem;
}

.new-contract-panel__field {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.new-contract-panel__field-label {
  font-family: var(--font-body);
  font-size: 11px;
  color: #94a3b8;
}

.new-contract-panel__toggle {
  display: flex;
  gap: 0.3rem;
  margin-bottom: 0.6rem;
}

.new-contract-panel__toggle-btn {
  flex: 1;
  background: #f8f9fc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.4rem;
  font-family: var(--font-body);
  font-size: 12px;
  color: #64748b;
  cursor: pointer;
}

.new-contract-panel__toggle-btn--active {
  background: #1e3a5f;
  border-color: #1e3a5f;
  color: #ffffff;
}

.new-contract-panel__tenant-search {
  position: relative;
}

.new-contract-panel__selected-tenant {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  padding: 0.5rem 0.6rem;
  margin-top: 0.4rem;
  font-family: var(--font-body);
  font-size: 13px;
  color: #1e3a5f;
}

.new-contract-panel__clear-tenant {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
}

.new-contract-panel__tenant-results {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  margin-top: 0.4rem;
  overflow: hidden;
}

.new-contract-panel__tenant-result {
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 0.5rem 0.6rem;
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--text-content);
  cursor: pointer;
}

.new-contract-panel__tenant-result:hover {
  background: #f8fafc;
}

.new-contract-panel__add-escalation {
  background: none;
  border: none;
  color: #3b82f6;
  font-family: var(--font-body);
  font-size: 12px;
  cursor: pointer;
  padding: 0;
}

.new-contract-panel__escalation-row {
  display: grid;
  grid-template-columns: 1fr 1fr 28px;
  gap: 0.4rem;
  align-items: center;
  margin-bottom: 0.4rem;
}

.new-contract-panel__remove-escalation {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
  justify-content: center;
}

.new-contract-panel__footer {
  flex-shrink: 0;
  display: flex;
  gap: 0.6rem;
  padding: 0.85rem 1.25rem;
  border-top: 1px solid #e2e8f0;
}

.new-contract-panel__cancel-btn {
  flex: 1;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.6rem;
  font-family: var(--font-body);
  font-size: 13px;
  color: #64748b;
  cursor: pointer;
}

.new-contract-panel__submit-btn {
  flex: 2;
  background: #3b82f6;
  border: none;
  border-radius: 6px;
  padding: 0.6rem;
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 500;
  color: #ffffff;
  cursor: pointer;
}

.new-contract-panel__submit-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
</style>